import React, { useState, useMemo } from 'react';

/**
 * Reubicación de técnicos — SOLO admin maestro (IGNOROSA).
 * La autorización real se valida en el backend; este componente solo es UI.
 * La región se deriva del catálogo al elegir distrito (y se vuelve a derivar en servidor).
 */
const field = { width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #CBD5E1', background: '#fff', fontSize: '14px', boxSizing: 'border-box' };
const label = { display: 'block', marginBottom: '8px', fontWeight: 600, color: '#475569', fontSize: '14px' };
const btn = (bg, disabled) => ({
  padding: '12px 24px', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: 700, fontSize: '15px',
  cursor: disabled ? 'default' : 'pointer', background: disabled ? '#94A3B8' : bg,
});

const RelocateTechnicianPanel = ({ apiUrl, distritosCatalog = [], isMobile = false }) => {
  const token = () => localStorage.getItem('auth_token') || localStorage.getItem('control_campo_token');

  const [usuario, setUsuario] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [preview, setPreview] = useState(null);
  const [newDistrito, setNewDistrito] = useState('');
  const [updateProfile, setUpdateProfile] = useState(true);
  const [comentario, setComentario] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [msg, setMsg] = useState(null); // {type, text}

  const distritoOptions = useMemo(
    () => [...distritosCatalog].sort((a, b) => a.distrito.localeCompare(b.distrito)),
    [distritosCatalog]
  );
  const selected = distritoOptions.find(d => d.distrito === newDistrito);
  const newRegion = selected ? selected.region : '';

  const authHeaders = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` });

  const errorText = async (res) => {
    try { const j = await res.json(); return typeof j.detail === 'string' ? j.detail : 'Error en la solicitud'; }
    catch { return 'Error en la solicitud'; }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setMsg(null); setPreview(null); setNewDistrito('');
    if (!usuario.trim() || !startDate || !endDate) { setMsg({ type: 'error', text: 'Captura usuario y rango de fechas.' }); return; }
    setLoading(true);
    try {
      const qs = new URLSearchParams({ usuario_ffm: usuario.trim().toUpperCase(), start_date: startDate, end_date: endDate });
      const res = await fetch(`${apiUrl}/admin/relocation/preview?${qs}`, { headers: authHeaders() });
      if (!res.ok) { setMsg({ type: 'error', text: await errorText(res) }); return; }
      setPreview(await res.json());
    } catch { setMsg({ type: 'error', text: 'No se pudo conectar con el servidor.' }); }
    finally { setLoading(false); }
  };

  const handleApply = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiUrl}/admin/relocation/apply`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          usuario_ffm: preview.usuario_ffm, start_date: startDate, end_date: endDate,
          new_distrito: newDistrito, update_profile: updateProfile, comentario: comentario || null,
        }),
      });
      if (!res.ok) { setMsg({ type: 'error', text: await errorText(res) }); return; }
      const r = await res.json();
      setMsg({ type: 'success', text: `✅ ${r.reports_updated} recorrido(s) movidos a ${r.region} / ${r.distrito}${r.profile_updated ? ' y perfil del técnico actualizado' : ''}.` });
      setPreview(null); setNewDistrito(''); setComentario('');
    } catch { setMsg({ type: 'error', text: 'No se pudo conectar con el servidor.' }); }
    finally { setLoading(false); setConfirmOpen(false); }
  };

  const canApply = preview && preview.total + (preview.full_name ? 1 : 0) > 0 && newDistrito;

  return (
    <div style={{ background: '#fff', borderRadius: '16px', padding: '28px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      <div style={{ display: 'flex', gap: '14px', alignItems: 'center', marginBottom: '24px' }}>
        <span style={{ fontSize: '32px' }}>🔀</span>
        <div>
          <h2 style={{ margin: 0, fontSize: '22px', color: '#1E293B' }}>Reubicar Técnico (Región / Distrito)</h2>
          <p style={{ margin: '4px 0 0', color: '#64748B', fontSize: '14px' }}>
            Corrige la ubicación de los recorridos de un técnico en un rango de fechas (segmentos, no realizables y justificaciones heredan el cambio). Solo administrador maestro.
          </p>
        </div>
      </div>

      {msg && (
        <div role="alert" style={{ padding: '12px 16px', borderRadius: '10px', marginBottom: '20px', fontWeight: 600,
          background: msg.type === 'success' ? '#DCFCE7' : '#FEE2E2', color: msg.type === 'success' ? '#166534' : '#991B1B' }}>
          {msg.text}
        </div>
      )}

      <form onSubmit={handleSearch} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr auto', gap: '16px', alignItems: 'end' }}>
        <div><label style={label} htmlFor="rl-user">Usuario FFM</label>
          <input id="rl-user" style={field} value={usuario} maxLength={40} placeholder="GBRMTPTVT0104" onChange={e => setUsuario(e.target.value.toUpperCase())} /></div>
        <div><label style={label} htmlFor="rl-start">Desde</label>
          <input id="rl-start" type="date" style={field} value={startDate} onChange={e => setStartDate(e.target.value)} /></div>
        <div><label style={label} htmlFor="rl-end">Hasta</label>
          <input id="rl-end" type="date" style={field} value={endDate} onChange={e => setEndDate(e.target.value)} /></div>
        <button id="rl-search" type="submit" disabled={loading} style={btn('linear-gradient(135deg,#3B82F6,#1D4ED8)', loading)}>
          {loading && !preview ? 'Buscando…' : '🔎 Buscar'}
        </button>
      </form>

      {preview && (
        <div style={{ marginTop: '28px' }}>
          <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '14px', padding: '20px', marginBottom: '20px' }}>
            <div style={{ fontWeight: 700, color: '#1E293B', fontSize: '16px' }}>{preview.full_name || 'Usuario sin perfil'} · {preview.usuario_ffm}</div>
            <div style={{ color: '#64748B', fontSize: '14px', margin: '4px 0 14px' }}>
              Perfil actual: <b>{preview.profile_region || '—'} / {preview.profile_distrito || '—'}</b> · Recorridos en el rango: <b>{preview.total}</b>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead><tr style={{ textAlign: 'left', color: '#64748B' }}>
                <th style={{ padding: '6px' }}>Región</th><th style={{ padding: '6px' }}>Distrito</th>
                <th style={{ padding: '6px' }}>Recorridos</th><th style={{ padding: '6px' }}>Del</th><th style={{ padding: '6px' }}>Al</th>
              </tr></thead>
              <tbody>
                {preview.groups.map((g, i) => (
                  <tr key={i} style={{ borderTop: '1px solid #E2E8F0' }}>
                    <td style={{ padding: '8px 6px' }}>{g.region}</td><td style={{ padding: '8px 6px' }}>{g.distrito}</td>
                    <td style={{ padding: '8px 6px', fontWeight: 700 }}>{g.count}</td>
                    <td style={{ padding: '8px 6px' }}>{g.first_date}</td><td style={{ padding: '8px 6px' }}>{g.last_date}</td>
                  </tr>
                ))}
                {preview.groups.length === 0 && <tr><td colSpan={5} style={{ padding: '8px 6px', color: '#94A3B8' }}>Sin recorridos en ese rango (solo se podría actualizar el perfil).</td></tr>}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap: '16px' }}>
            <div><label style={label} htmlFor="rl-dist">Nuevo distrito</label>
              <select id="rl-dist" style={field} value={newDistrito} onChange={e => setNewDistrito(e.target.value)}>
                <option value="">-- Selecciona distrito --</option>
                {distritoOptions.map(d => <option key={d.distrito} value={d.distrito}>{d.distrito}</option>)}
              </select></div>
            <div><label style={label} htmlFor="rl-reg">Región (automática)</label>
              <input id="rl-reg" style={{ ...field, background: '#F1F5F9' }} value={newRegion} readOnly placeholder="—" /></div>
          </div>

          <div style={{ marginTop: '16px' }}><label style={label} htmlFor="rl-com">Comentario (opcional, queda en historial)</label>
            <input id="rl-com" style={field} value={comentario} maxLength={500} onChange={e => setComentario(e.target.value)} /></div>

          <label style={{ display: 'flex', gap: '10px', alignItems: 'center', margin: '16px 0', color: '#334155', fontSize: '14px' }}>
            <input type="checkbox" checked={updateProfile} onChange={e => setUpdateProfile(e.target.checked)} />
            Actualizar también el perfil del técnico (sus recorridos futuros quedarán en el nuevo distrito)
          </label>

          <button id="rl-apply" type="button" disabled={!canApply || loading} onClick={() => setConfirmOpen(true)}
            style={btn('linear-gradient(135deg,#F59E0B,#D97706)', !canApply || loading)}>
            Aplicar cambio…
          </button>
        </div>
      )}

      {confirmOpen && preview && (
        <div role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '16px' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '28px', maxWidth: '480px', width: '100%' }}>
            <h3 style={{ marginTop: 0, color: '#1E293B' }}>⚠️ Confirmar reubicación</h3>
            <p style={{ color: '#475569', lineHeight: 1.5 }}>
              A <b>{preview.full_name || preview.usuario_ffm}</b> ({preview.usuario_ffm}) se le actualizarán <b>{preview.total}</b> recorrido(s)
              del <b>{startDate}</b> al <b>{endDate}</b> para que queden en <b>{newRegion} / {newDistrito}</b>
              {updateProfile ? ', y se actualizará su perfil' : ''}. ¿Estás seguro?
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button id="rl-cancel" type="button" onClick={() => setConfirmOpen(false)} disabled={loading} style={btn('#64748B', loading)}>Cancelar</button>
              <button id="rl-confirm" type="button" onClick={handleApply} disabled={loading} style={btn('linear-gradient(135deg,#EF4444,#B91C1C)', loading)}>
                {loading ? 'Aplicando…' : 'Sí, aplicar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RelocateTechnicianPanel;
